import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadWalletModalBase } from '@shared/components/load-wallet/load-wallet-modal-base';
import { PaymentMethodsList } from '@modules/settings/subscription/components/payment-methods-list';
import { WALLET_LIMIT } from '@shared/constants';
import { UseModalReturn } from '@shared/hooks/use-modal';
import { UsePaymentMethodsListReturn } from '@modules/settings/subscription/hooks/use-payment-methods-list';
import { UseLoadWalletReturn } from '@modules/settings/my-numbers/my-numbers.types';
import { capitalize } from '@shared/utils/capitalize';

type Props = {
  payModal: UseModalReturn;
  paymentMethodsList: UsePaymentMethodsListReturn;
  loadWallet: UseLoadWalletReturn;
  totalAmount: number;
  onLoaded: VoidFunction;
};

export function ConfirmPaymentLoadWalletModal({
  payModal,
  paymentMethodsList,
  loadWallet,
  totalAmount,
  onLoaded,
}: Props) {
  const { t } = useTranslation();
  const [purchaseAmount, setPurchaseAmount] = useState(() => Math.max(totalAmount, WALLET_LIMIT.LOAD_WALLET));
  const [limitError, setLimitError] = useState('');

  const handleAmountChange = (val: number) => {
    setPurchaseAmount(val || 0);
    setLimitError(
      val < WALLET_LIMIT.LOAD_WALLET ? t('minimum_load_amount_is_{amount}', { amount: WALLET_LIMIT.LOAD_WALLET }) : ''
    );
  };

  const handleCancel = () => {
    payModal.handleClose();
    setLimitError('');
  };

  const handleConfirm = async () => {
    if (purchaseAmount < WALLET_LIMIT.LOAD_WALLET) {
      setLimitError(t('minimum_load_amount_is_{amount}', { amount: WALLET_LIMIT.LOAD_WALLET }));
      return;
    }
    await loadWallet.loadWallet(purchaseAmount, paymentMethodsList, () => {
      payModal.handleClose();
      onLoaded();
    });
  };

  return (
    <LoadWalletModalBase
      open={payModal.open}
      onCancel={handleCancel}
      subtitle={t('add_credit_to_your_wallet_to_continue_your_purchase')}
      amount={purchaseAmount}
      onAmountChange={handleAmountChange}
      limitError={limitError}
      paymentMethodsList={paymentMethodsList}
      onConfirm={handleConfirm}
      confirmLoading={paymentMethodsList.processing || loadWallet.loadWalletLoading}
      confirmLabel={capitalize(t('load_wallet_and_continue'))}
      complianceFooter={<PaymentMethodsList.CompliantLabel />}
    />
  );
}
